import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2, Sparkles, TrendingUp, Clock, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useSearchContext } from '@/contexts/SearchContext';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface GlobalSearchBarProps {
  className?: string;
  placeholder?: string;
  showInNav?: boolean;
}

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  className,
  placeholder = "Search AI tools... (e.g., 'coding assistant', 'image generator')",
  showInNav = false
}) => {
  const navigate = useNavigate();
  const { 
    query, 
    setQuery, 
    suggestions, 
    fetchSuggestions, 
    performSearch,
    isFetchingSuggestions,
    clearSearch
  } = useSearchContext();
  
  const [localQuery, setLocalQuery] = useState(query);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    debounceRef.current = setTimeout(() => {
      if (localQuery.length >= 2) {
        fetchSuggestions(localQuery);
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [localQuery, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;
    setQuery(searchQuery);
    setIsOpen(false);
    performSearch(searchQuery);
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  }, [setQuery, performSearch, navigate]);

  const totalCount = suggestions.tools.length + suggestions.aiSuggestions.length + suggestions.popularSearches.length;

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, totalCount - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.tools.length) {
          navigate(`/bot/${suggestions.tools[selectedIndex].id}`);
        } else if (selectedIndex >= suggestions.tools.length && selectedIndex < suggestions.tools.length + suggestions.aiSuggestions.length) {
          handleSearch(suggestions.aiSuggestions[selectedIndex - suggestions.tools.length]);
        } else if (selectedIndex >= suggestions.tools.length + suggestions.aiSuggestions.length) {
          handleSearch(suggestions.popularSearches[selectedIndex - suggestions.tools.length - suggestions.aiSuggestions.length]);
        } else {
          handleSearch(localQuery);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  }, [suggestions, selectedIndex, localQuery, handleSearch, navigate, totalCount]);

  const handleClear = () => {
    setLocalQuery('');
    clearSearch();
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div className="relative group">
        <Input
          ref={inputRef}
          type="text"
          value={localQuery}
          onChange={(e) => setLocalQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => localQuery.length >= 2 && setIsOpen(true)}
          placeholder={placeholder}
          className={cn(
            "pl-12 pr-24 transition-all duration-300",
            showInNav 
              ? "h-10 text-sm rounded-full bg-muted/50 border-transparent focus:border-primary focus:bg-background"
              : "h-14 sm:h-16 text-base sm:text-lg rounded-full bg-background/80 backdrop-blur-sm border border-primary/30 shadow-lg focus:border-primary focus:ring-2 focus:ring-primary/30 group-hover:border-primary/60 group-hover:shadow-xl"
          )}
        />
        <Search className={cn(
          "absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors",
          showInNav ? "w-4 h-4" : "w-5 h-5 sm:w-6 sm:h-6",
          localQuery && "text-primary"
        )} />
        
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {isFetchingSuggestions && (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          )}
          {localQuery && (
            <Button variant="ghost" size="icon" onClick={handleClear} className="h-8 w-8 rounded-full hover:bg-muted">
              <X className="w-4 h-4" />
            </Button>
          )}
          {!showInNav && (
            <Button
              onClick={() => handleSearch(localQuery)}
              size="lg"
              className="h-10 sm:h-12 px-5 sm:px-8 text-sm sm:text-base rounded-full bg-primary hover:opacity-90 shadow-md font-semibold transition-all"
            >
              Search
            </Button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isOpen && totalCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-50"
          >
            {suggestions.tools.length > 0 && (
              <div className="p-2">
                <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase">AI Tools</div>
                {suggestions.tools.map((tool, index) => (
                  <button
                    key={tool.id}
                    onClick={() => navigate(`/bot/${tool.id}`)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left",
                      selectedIndex === index ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    )}
                  >
                    {tool.thumbnail ? (
                      <img src={tool.thumbnail} alt="" className="w-8 h-8 rounded-lg object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{tool.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{tool.use_case}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}

            {suggestions.aiSuggestions.length > 0 && (
              <div className="p-2 border-t border-border">
                <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI Suggestions
                </div>
                {suggestions.aiSuggestions.map((suggestion, index) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSearch(suggestion)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left",
                      selectedIndex === suggestions.tools.length + index ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    )}
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                      <Search className="w-4 h-4 text-primary" />
                    </div>
                    <span className="flex-1">{suggestion}</span>
                  </button>
                ))}
              </div>
            )}

            {suggestions.popularSearches.length > 0 && (
              <div className="p-2 border-t border-border">
                <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Popular
                </div>
                {suggestions.popularSearches.map((search, index) => (
                  <button
                    key={search}
                    onClick={() => handleSearch(search)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left",
                      selectedIndex === suggestions.tools.length + suggestions.aiSuggestions.length + index ? "bg-primary/10 text-primary" : "hover:bg-muted"
                    )}
                  >
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <span className="flex-1">{search}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="px-4 py-2 bg-muted/50 border-t border-border text-xs text-muted-foreground flex items-center justify-between">
              <span>Press Enter to search</span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-background rounded text-xs">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-background rounded text-xs">↓</kbd>
                to navigate
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
